<template>
  <teleport to="body">
    <transition name="widget-tooltip-fade">
      <div
        v-if="visible"
        ref="tooltipRef"
        class="widget-tooltip"
        :class="{
          'widget-tooltip--error': variant === 'error',
          'widget-tooltip--info': variant === 'info',
        }"
        :style="positionStyle"
      >
        <div class="widget-tooltip__content">
          <span class="widget-tooltip__text">
            <slot>{{ text }}</slot>
          </span>
        </div>
        <div
          class="widget-tooltip__arrow"
          :class="[
            `widget-tooltip__arrow--${actualPosition}`,
            {
              'widget-tooltip__arrow--error': variant === 'error',
              'widget-tooltip__arrow--info': variant === 'info',
            },
          ]"
        />
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { nextTick, ref, watch, onMounted, onUnmounted } from '@/composition/vue/compositionApi'

const {
  visible = false,
  text = '',
  target = null,
  position = 'top',
  offset = 8,
  variant = 'error',
} = defineProps<{
  visible?: boolean
  text?: string
  target?: HTMLElement | null
  position?: 'top' | 'bottom' | 'left' | 'right'
  offset?: number
  variant?: 'error' | 'info'
}>()

const tooltipRef = ref<HTMLElement>()
const positionStyle = ref({})
const actualPosition = ref<'top' | 'bottom' | 'left' | 'right'>(position)

const calculatePosition = async () => {
  if (!visible || !target) {
    return
  }

  await nextTick()

  if (!tooltipRef.value) {
    return
  }

  const targetRect = target.getBoundingClientRect()
  const tooltipRect = tooltipRef.value.getBoundingClientRect()
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let top = 0
  let left = 0
  let currentPosition = position

  switch (position) {
    case 'top':
      top = targetRect.top - tooltipRect.height - offset
      left = targetRect.left + (targetRect.width - tooltipRect.width) / 2
      break
    case 'bottom':
      top = targetRect.bottom + offset
      left = targetRect.left + (targetRect.width - tooltipRect.width) / 2
      break
    case 'left':
      top = targetRect.top + (targetRect.height - tooltipRect.height) / 2
      left = targetRect.left - tooltipRect.width - offset
      break
    case 'right':
      top = targetRect.top + (targetRect.height - tooltipRect.height) / 2
      left = targetRect.right + offset
      break
    default:
      break
  }

  // Keep tooltip within the viewing area
  if (left < 0) {
    left = 8
  } else if (left + tooltipRect.width > viewportWidth) {
    left = viewportWidth - tooltipRect.width - 8
  }

  if (top < 0) {
    top = 8
  } else if (top + tooltipRect.height > viewportHeight) {
    top = viewportHeight - tooltipRect.height - 8
  }

  const tooltipBottom = top + tooltipRect.height
  const tooltipRight = left + tooltipRect.width
  const isOverlapping = !(
    tooltipRight < targetRect.left ||
    left > targetRect.right ||
    tooltipBottom < targetRect.top ||
    top > targetRect.bottom
  )

  if (isOverlapping) {
    // Try positioning below target
    if (targetRect.bottom + offset + tooltipRect.height <= viewportHeight) {
      top = targetRect.bottom + offset
      left = targetRect.left + (targetRect.width - tooltipRect.width) / 2
      currentPosition = 'bottom'
    }
    // Otherwise try above
    else if (targetRect.top - offset - tooltipRect.height >= 0) {
      top = targetRect.top - tooltipRect.height - offset
      left = targetRect.left + (targetRect.width - tooltipRect.width) / 2
      currentPosition = 'top'
    }
    // Otherwise position to the right
    else if (targetRect.right + offset + tooltipRect.width <= viewportWidth) {
      left = targetRect.right + offset
      top = targetRect.top + (targetRect.height - tooltipRect.height) / 2
      currentPosition = 'right'
    }
    // Otherwise position to the left
    else if (targetRect.left - offset - tooltipRect.width >= 0) {
      left = targetRect.left - tooltipRect.width - offset
      top = targetRect.top + (targetRect.height - tooltipRect.height) / 2
      currentPosition = 'left'
    }
  }

  actualPosition.value = currentPosition

  positionStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
  }
}

watch(
  () => [visible, target, position],
  () => {
    actualPosition.value = position
    if (visible && target) {
      calculatePosition()
    }
  },
  { immediate: true },
)

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  window.addEventListener('resize', calculatePosition)
  window.addEventListener('scroll', calculatePosition)

  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver(calculatePosition)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', calculatePosition)
  window.removeEventListener('scroll', calculatePosition)
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})

watch(
  () => target,
  (newTarget, oldTarget) => {
    if (resizeObserver) {
      if (oldTarget) {
        resizeObserver.unobserve(oldTarget)
      }
      if (newTarget) {
        resizeObserver.observe(newTarget)
      }
    }
  },
)
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';

.widget-tooltip {
  position: fixed;
  color: white;
  border-radius: $border-radius-x;
  font-size: 12px;
  line-height: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-width: 280px;
  word-wrap: break-word;
  overflow: visible;
  pointer-events: none;
  z-index: 10000;

  &--error {
    background: $error;
  }

  &--info {
    background: rgba(0, 0, 0, 0.85);
  }

  &__content {
    padding: 8px 12px;
    overflow: hidden;
    max-height: 300px;
  }

  &__text {
    width: 100%;
    text-align: center;
  }

  &__arrow {
    position: absolute;
    width: 0;
    height: 0;

    &--top {
      bottom: -4px;
      left: 50%;
      transform: translateX(-50%);
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;

      &.widget-tooltip__arrow--error {
        border-top: 4px solid $error;
      }

      &.widget-tooltip__arrow--info {
        border-top: 4px solid rgba(0, 0, 0, 0.85);
      }
    }

    &--bottom {
      top: -4px;
      left: 50%;
      transform: translateX(-50%);
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;

      &.widget-tooltip__arrow--error {
        border-bottom: 4px solid $error;
      }

      &.widget-tooltip__arrow--info {
        border-bottom: 4px solid rgba(0, 0, 0, 0.85);
      }
    }

    &--left {
      right: -4px;
      top: 50%;
      transform: translateY(-50%);
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;

      &.widget-tooltip__arrow--error {
        border-left: 4px solid $error;
      }

      &.widget-tooltip__arrow--info {
        border-left: 4px solid rgba(0, 0, 0, 0.85);
      }
    }

    &--right {
      left: -4px;
      top: 50%;
      transform: translateY(-50%);
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;

      &.widget-tooltip__arrow--error {
        border-right: 4px solid $error;
      }

      &.widget-tooltip__arrow--info {
        border-right: 4px solid rgba(0, 0, 0, 0.85);
      }
    }
  }
}

.widget-tooltip-fade-enter-active,
.widget-tooltip-fade-leave-active {
  transition: opacity 0.2s ease;
}

.widget-tooltip-fade-enter-from,
.widget-tooltip-fade-leave-to {
  opacity: 0;
}
</style>
